import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDataTable, SpecraMetricStrip } from '@mono/ui-components/captains';
import { SpecraButton, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { useState } from 'react';
import { money } from '../ratesView';
import { ParameterField } from './ParameterField';
export function GroupSection({ group, columns, periods, onChange, removable, onRemove, locationVariants }) {
    const [expanded, setExpanded] = useState(true);
    const update = (line)=>onChange({
            ...group,
            lines: group.lines.map((l)=>l.id === line.id ? line : l)
        });
    const currentFtes = group.lines.reduce((sum, line)=>sum + (line.fteCount ?? 0), 0);
    const pc = group.partitionConstraints;
    const warning = pc && (pc.minFtes !== undefined && currentFtes < pc.minFtes || pc.maxFtes !== undefined && currentFtes > pc.maxFtes);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: `${group.label} · ${group.lines.length} roles`,
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "xs",
                    onClick: ()=>setExpanded(!expanded),
                    children: expanded ? 'Collapse' : 'Expand'
                })
            }),
            warning && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "warning",
                children: [
                    currentFtes,
                    " FTEs; required ",
                    pc.minFtes ?? '—',
                    "–",
                    pc.maxFtes ?? '—'
                ]
            }),
            expanded && /*#__PURE__*/ _jsx(SpecraDataTable, {
                rows: group.lines,
                rowKey: (l)=>l.id,
                getRowLabel: (l)=>l.label,
                columns: [
                    ...columns.flatMap((col)=>(col.perPeriod && periods.length ? periods : [
                            undefined
                        ]).map((period)=>({
                                id: `${col.key}-${period ?? ''}`,
                                header: `${col.label}${period ? ` (${period})` : ''}`,
                                cell: (line)=>{
                                    if (col.key === 'label') return /*#__PURE__*/ _jsx(SpecraText, {
                                        children: line.label
                                    });
                                    if (col.key === 'ftes') return /*#__PURE__*/ _jsx(ParameterField, {
                                        label: `${line.label} FTEs`,
                                        param: {
                                            name: 'ftes',
                                            label: 'FTEs',
                                            value: line.fteCount ?? null,
                                            paramType: 'quantity',
                                            source: 'contractor',
                                            perPeriod: false,
                                            constraints: {
                                                min: 0
                                            }
                                        },
                                        editable: true,
                                        onChange: (value)=>update({
                                                ...line,
                                                fteCount: value ?? undefined
                                            })
                                    });
                                    if (col.key === 'location' && locationVariants?.length) return /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        "aria-label": `${line.label} location`,
                                        value: line.location ?? '',
                                        onChange: (event)=>update({
                                                ...line,
                                                location: event.target.value || undefined
                                            }),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "—"
                                            }),
                                            locationVariants.map((loc)=>/*#__PURE__*/ _jsx("option", {
                                                    value: loc,
                                                    children: loc === 'on_site' ? 'On-site' : 'Off-site'
                                                }, loc))
                                        ]
                                    });
                                    const param = line.parameters[col.parameterName];
                                    if (!param) return '—';
                                    return /*#__PURE__*/ _jsx(ParameterField, {
                                        label: `${line.label} ${col.label}${period ? ` ${period}` : ''}`,
                                        param: param,
                                        period: period,
                                        editable: col.editable,
                                        onChange: (value)=>update({
                                                ...line,
                                                parameters: {
                                                    ...line.parameters,
                                                    [col.parameterName]: period && param.perPeriod ? {
                                                        ...param,
                                                        periodValues: {
                                                            ...param.periodValues,
                                                            [period]: value ?? 0
                                                        }
                                                    } : {
                                                        ...param,
                                                        value
                                                    }
                                                }
                                            })
                                    });
                                }
                            }))),
                    ...removable ? [
                        {
                            id: 'actions',
                            header: 'Actions',
                            cell: (line)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "xs",
                                    variant: "ghost",
                                    "aria-label": `Remove ${line.label}`,
                                    onClick: ()=>onRemove?.(line.id),
                                    children: "Remove"
                                })
                        }
                    ] : []
                ]
            }),
            expanded && group.subtotal && /*#__PURE__*/ _jsxs(SpecraText, {
                children: [
                    "Subtotal: ",
                    money(String(group.subtotal.value ?? 0))
                ]
            })
        ]
    });
}
export function CostSummary({ clinPricing }) {
    return /*#__PURE__*/ _jsx(SpecraMetricStrip, {
        density: "compact",
        metrics: [
            ...Object.entries(clinPricing.periodTotals).map(([period, value])=>({
                    id: period,
                    label: period,
                    value: money(String(value.totalCost)),
                    tone: 'neutral'
                })),
            {
                id: 'total',
                label: 'CLIN total · preview',
                value: money(String(clinPricing.grandTotal)),
                tone: 'neutral'
            }
        ]
    });
}
